import React, { useId } from 'react';
import { Tooltip } from '@allygpo/ally-ux';

interface TipProps {
  /** Tooltip text. Nothing is attached when empty. */
  text?: string | null;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

/**
 * Wraps content in the app's tooltip (dark pill) instead of a native `title`, which renders in
 * the browser's own style. The wrapper also receives hover when the child is disabled.
 */
const Tip: React.FC<TipProps> = ({ text, children, position = 'top', className }) => {
  const id = `tip${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  if (!text) return <>{children}</>;
  return (
    <span className={`siq-tip inline-flex max-w-full${className ? ` ${className}` : ''}`} data-tip-id={id} data-pr-tooltip={text} data-pr-position={position}>
      <Tooltip position="top" target={`[data-tip-id="${id}"]`} />
      {children}
    </span>
  );
};

/** Attributes + Tooltip for elements that can't be wrapped (e.g. a styled pill span). */
export const useTip = (text?: string | null, position: 'top' | 'bottom' | 'left' | 'right' = 'top') => {
  const id = `tip${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  if (!text) return { attrs: {}, tooltip: null };
  return {
    attrs: { 'data-tip-id': id, 'data-pr-tooltip': text, 'data-pr-position': position },
    tooltip: <Tooltip position="top" target={`[data-tip-id="${id}"]`} />,
  };
};

export default Tip;
